import React, { useEffect, useMemo, useRef, useState } from "react"
import {
  getAccountingDashboard,
  getAccountingSchedule
} from "../../../../../features/accounting/accounting"
import { showSnackBar } from "../../../../../components/Snackbar/Snackbar"
import { useDispatch } from "react-redux"
import { useMyContextLoading } from "../../../../../components/LoadingContextProvider/LoadingContextProvider"
import TableFooter from "../../../../../components/Pagination/TableFooter"
import TableList from "../TableList"
import { PaymentPlanIcon } from "../../../../../components/Icons/Icons"
import { formatMoney } from "../../../../../utils/amount"
import Filters from "../../../../../components/Filters/Filters"
import { initQueryData } from "../../../../../components/Filters/helper"
import QuickLinks from "../../../../../components/QuickLinks/QuickLinks"
import useURLState from "../../../../../hooks/useURLState"
import { TABLE_CARD } from "../../../../../components/Table/tableCardClass"


const Schedule = ({ userRole }) => {
  const dispatch = useDispatch()
  const { triggerLoading, triggerBar } = useMyContextLoading()

  const [valuePagination, setValuePagination] = useState(10)
  const [pagePagination, setPagePagination] = useState(1)
  const [pageCount, setPageCount] = useState(0)
  const [totalCount, setTotalCount] = useState(null)
  const [schedulePayments, setSchedulePayments] = useState([])
  const [requestFilters, setRequestFilters] = useState([initQueryData])
  const [filterData, setFilterData] = useState({ filters: [], utils: [] })
  const filtersKey = useMemo(() => JSON.stringify(requestFilters), [requestFilters])
  const isFetching = useRef(false)
  const { sortField, sortOrder, setMultiple: setSortMultiple } = useURLState({
    sortField: { type: 'string', default: '' },
    sortOrder: { type: 'string', default: '' },
  })
  const [dashboardData, setDashboardData] = useState({
    cleared: 0,
    processing: 0,
    failed: 0,
    scheduled: 0,
  })

  const handleSort = (field, order) => {
    setSortMultiple({ sortField: field, sortOrder: order })
    setPagePagination(1)
  }

  const getAcSchedule = async ({ filterListId = null }) => {
    triggerBar(true)
    try {
      const limit = parseInt(valuePagination)
      const offset = (pagePagination - 1) * limit
      const hasFilters = requestFilters?.length > 0 && requestFilters.some(f => f.value || f.operator)
      let filters = hasFilters ? requestFilters : ""

      let result = await dispatch(getAccountingSchedule({
        limit: valuePagination,
        offset,
        filters,
        filterListId,
        sortField,
        sortOrder,
      }))
      if (result?.error) {
        const errorMessage = result?.payload || 'An error occurred. Please try again.'
        showSnackBar("error", errorMessage)
      } else if (result?.payload?.data) {
        setSchedulePayments(result?.payload?.data?.payments)
        setPageCount(result?.payload?.data?.meta?.pageCount)
        setTotalCount(result?.payload?.data?.meta?.totalPayments)
      }
    } catch
    (error) {
      showSnackBar('error', 'An unexpected error occurred. Please try again.')
    }
    triggerBar(false)
  }

  const fetchDashboardData = async ({ filterListId = null }) => {
    try {
      const hasFilters = requestFilters?.length > 0 && requestFilters.some(f => f.value || f.operator)
      let filters = hasFilters ? requestFilters : ""

      let result = await dispatch(getAccountingDashboard({ filters, filterListId }))
      if (result?.error) {
        const errorMessage = result?.payload || 'An error occurred. Please try again.'
        showSnackBar("error", errorMessage)
      } else if (result?.payload?.data) {
        setDashboardData(result?.payload?.data)
      }
    } catch (error) {
      showSnackBar('error', 'An unexpected error occurred. Please try again.')
    }
  }

  const handleQuickLinksChange = (newFilters) => {
    setRequestFilters(newFilters.length > 0 ? newFilters : [initQueryData])
  }

  // Filter and rows-per-page changes keep the current page — the Pagination
  // component clamps to the last page when the new page count is smaller, so
  // the operator never lands past the end (and never gets yanked back to page 1).
  const applyFilters = (newFilters) => {
    setRequestFilters(newFilters)
  }

  const fetch = (filterListId = null) => {
    if (isFetching.current) return
    isFetching.current = true
    Promise.all([getAcSchedule({ filterListId }), fetchDashboardData({ filterListId })]).finally(() => {
      isFetching.current = false
    })
  }

  useEffect(() => {
    fetch(null)
  }, [pagePagination, valuePagination, filtersKey, sortField, sortOrder])
  const isValidNumber = (value) => !isNaN(parseFloat(value)) && isFinite(value)


  return (
    <div>
      <div className="flex flex-row justify-between items-center w-full">
        <Filters isFilterList={true} fetchByFilters={applyFilters} fetchByFilterList={fetch}
          requestFilters={requestFilters} flow="payments"
          onFilterDataLoaded={setFilterData} />
        <div className="bg-surface border border-divider rounded-2xl shadow-custom my-4 py-4 max-w-max">
          <div className="flex px-4 gap-4 items-center">
            <PaymentPlanIcon bgFill="var(--color-info-background)" fill="var(--color-info-main)" />
            <div className="flex flex-col">
              <span className="font-bold text-lg-18 mb-1">
                {formatMoney(isValidNumber(dashboardData?.scheduled)
                  ? dashboardData?.scheduled
                  : 0)
                }
              </span>
              <span className="text-md">Total Pending</span>
            </div>
          </div>
        </div>
      </div>
      <div className="mb-2">
        <QuickLinks
          requestFilters={requestFilters}
          onFiltersChange={handleQuickLinksChange}
          filterDefs={filterData.filters} utilDefs={filterData.utils}
        />
      </div>
      <div className={TABLE_CARD}>
        <TableList
          triggerUpdate={(e) => { e && fetch() }}
          data={schedulePayments}
          userRole={userRole}
          showTransactionalId={false}
          sortField={sortField}
          sortOrder={sortOrder}
          onSort={handleSort}
        />
        <TableFooter page={pagePagination} limit={valuePagination} total={totalCount}
          pageCount={pageCount} setPage={setPagePagination} setLimit={setValuePagination} />
      </div>
    </div>
  )
}

export default Schedule